import { ChevronLeft as ChevronLeftIcon } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import {
  PtPatientOut,
  useIguanaApiV1ImportCoursesMutation,
} from "@providers/hop-ord-server/api";
import { useCallback, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { FormContainer } from "react-hook-form-mui";
import { SiteOption } from "../types";
import SelectPatientContent from "./SelectPatientContent";
import SelectPrescriptionContent from "./SelectPrescriptionContent";
import { useNavigate } from "react-router-dom";

export type ModalContentState = "select-patient" | "select-rx";

interface Props {
  open: boolean;
  onClose: () => void;
  treatingClinics: SiteOption[];
}

const ImportAriaPrescriptionsModal = ({
  open,
  onClose,
  treatingClinics,
}: Props) => {
  const nav = useNavigate();
  const [modalContentState, setModalContentState] =
    useState<ModalContentState>("select-patient");

  const [importCourses, { isLoading: _isImportingCourses }] =
    useIguanaApiV1ImportCoursesMutation();

  const [patients, setPatients] = useState<PtPatientOut[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<
    PtPatientOut | undefined
  >(undefined);

  const form = useForm<{
    clinic: SiteOption | null;
    searchPatient: string;
    selectedCourses: { [key: string]: boolean };
  }>({
    defaultValues: {
      clinic: null,
      searchPatient: "",
      selectedCourses: {},
    },
  });

  useEffect(() => {
    if (treatingClinics.length === 1) {
      form.setValue("clinic", treatingClinics[0]);
    }
  }, [treatingClinics, form, open]);

  const handleContinue = () => {
    setModalContentState("select-rx");
  };

  const handleImport = useCallback(async () => {
    const selectedCourses = Object.keys(form.getValues("selectedCourses"));
    const selectedClinic = form.getValues("clinic");

    if (
      selectedCourses.length === 0 ||
      !selectedPatient?.patientId ||
      selectedClinic === null
    ) {
      return;
    }

    const response = await importCourses({
      mrn: selectedPatient.patientId,
      tenantId: selectedClinic.tenantId,
      treatingDepartment: selectedClinic.value,
      selectedCourses: selectedCourses,
    });
    if (response.error) {
      // TODO: do something CRAZY
    }
    nav(`/order/${response.data}`);
  }, [nav, form, importCourses, selectedPatient]);

  const handleReset = () => {
    setModalContentState("select-patient");
    setPatients([]);
    setSelectedPatient(undefined);
    form.reset();
  };

  const selectedClinic = useWatch({ control: form.control, name: "clinic" });
  const selectedCourses = useWatch({
    control: form.control,
    name: "selectedCourses",
  });

  return (
    <Dialog
      open={open}
      fullWidth
      onTransitionExited={handleReset}
      maxWidth="xs"
    >
      <DialogTitle>Import Prescriptions</DialogTitle>
      <DialogContent>
        <FormContainer formContext={form}>
          {modalContentState === "select-patient" && (
            <SelectPatientContent
              clinics={treatingClinics}
              patients={patients}
              setPatients={setPatients}
              selectedPatientId={selectedPatient?.patientId || undefined}
              handleSelectPatient={setSelectedPatient}
              form={form}
            />
          )}
          {modalContentState === "select-rx" &&
            selectedPatient &&
            selectedClinic && (
              <SelectPrescriptionContent
                patient={selectedPatient}
                tenantId={selectedClinic.tenantId}
                clinic={selectedClinic.value}
                form={form}
              />
            )}
        </FormContainer>
      </DialogContent>
      <DialogActions>
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            width: 1,
          }}
        >
          <Button
            onClick={() => setModalContentState("select-patient")}
            startIcon={<ChevronLeftIcon />}
            sx={{
              visibility:
                modalContentState === "select-patient" ? "hidden" : "visible",
            }}
          >
            Back
          </Button>
          <Stack direction="row">
            <Button onClick={onClose}>Cancel</Button>
            {modalContentState === "select-patient" && (
              <Button onClick={handleContinue} disabled={!selectedPatient}>
                Continue
              </Button>
            )}
            {modalContentState === "select-rx" && (
              <Button
                onClick={handleImport}
                disabled={
                  Object.values(selectedCourses).filter(Boolean).length === 0
                }
              >
                Import
              </Button>
            )}
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default ImportAriaPrescriptionsModal;
